import { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Helmet } from 'react-helmet-async';
import { calculateDateNumerology, getDayOfYear, getDaysLeftInYear, calculateDuration } from '../../utils/dateNumerology';
import { saveDateToResearch, getResearchCollection } from '../../utils/researchStorage';
import { useAppTheme } from '../../utils/useAppTheme';
import WebNumberMeaning from '../components/WebNumberMeaning';

export default function DateCalculatorPage() {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [startMonth, setStartMonth] = useState('9');
  const [startDay, setStartDay] = useState('1');
  const [startYear, setStartYear] = useState('1990');
  const [endMonth, setEndMonth] = useState('1');
  const [endDay, setEndDay] = useState('11');
  const [endYear, setEndYear] = useState('2026');
  const [includeEndDate, setIncludeEndDate] = useState(true);
  const [showYear, setShowYear] = useState(true);
  const [showMonth, setShowMonth] = useState(true);
  const [showWeek, setShowWeek] = useState(true);
  const [showDay, setShowDay] = useState(true);
  const [results, setResults] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loadId = params.get('load');
    if (loadId) {
      const loadFromResearch = async () => {
        const research = await getResearchCollection();
        const entry = research.find(e => e.id === loadId);
        if (entry && entry.type === 'date') {
          const start = new Date(entry.startDate);
          const end = new Date(entry.endDate);
          setStartMonth((start.getMonth() + 1).toString());
          setStartDay(start.getDate().toString());
          setStartYear(start.getFullYear().toString());
          setEndMonth((end.getMonth() + 1).toString());
          setEndDay(end.getDate().toString());
          setEndYear(end.getFullYear().toString());
          setIncludeEndDate(entry.includeEndDate);
          
          // Clear param from URL without refreshing
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      };
      loadFromResearch();
    }
  }, []);

  const parseDate = (month, day, year) => {
    const m = parseInt(month) || 1;
    const d = parseInt(day) || 1;
    const y = parseInt(year) || 2024;
    return new Date(y, m - 1, d);
  };

  useEffect(() => {
    try {
      const start = parseDate(startMonth, startDay, startYear);
      const end = parseDate(endMonth, endDay, endYear);
      const duration = calculateDuration(start, end, includeEndDate);
      const startNumerology = calculateDateNumerology(startMonth, startDay, startYear);
      const endNumerology = calculateDateNumerology(endMonth, endDay, endYear);
      setResults({
        duration,
        startNumerology,
        endNumerology,
        startDayOfYear: getDayOfYear(start),
        startDaysLeft: getDaysLeftInYear(start),
        endDayOfYear: getDayOfYear(end),
        endDaysLeft: getDaysLeftInYear(end),
      });
    } catch (error) {
      console.error('Calculation error:', error);
    }
  }, [startMonth, startDay, startYear, endMonth, endDay, endYear, includeEndDate]);

  const formatDate = (month, day, year) => {
    try {
      const date = parseDate(month, day, year);
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Invalid Date';
    }
  };

  const handleSaveToResearch = async () => {
    if (!results) return;
    try {
      const start = parseDate(startMonth, startDay, startYear);
      const end = parseDate(endMonth, endDay, endYear);
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      const title = `${start.toLocaleDateString(undefined, options)} — ${end.toLocaleDateString(undefined, options)}`;
      
      await saveDateToResearch(
        title,
        start.toISOString(),
        end.toISOString(),
        includeEndDate,
        results.duration
      );
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      alert(error.message);
    }
  };

  const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[(parseInt(month) || 1) - 1] || 'Jan';
  };

  const getDurationDisplay = () => {
    if (!results) return '';
    const { totalDays, startDate } = results.duration;
    let remaining = totalDays;
    let curr = new Date(startDate);
    const parts = [];

    if (showYear) {
      let years = 0;
      while (true) {
        let next = new Date(curr);
        next.setFullYear(curr.getFullYear() + 1);
        let diff = Math.round((next - curr) / 86400000);
        if (diff <= remaining) {
          years++;
          remaining -= diff;
          curr = next;
        } else break;
      }
      if (years > 0) parts.push(years + ' Year' + (years !== 1 ? 's' : ''));
    }

    if (showMonth) {
      let months = 0;
      while (true) {
        let next = new Date(curr);
        next.setMonth(curr.getMonth() + 1);
        let diff = Math.round((next - curr) / 86400000);
        if (diff <= remaining) {
          months++;
          remaining -= diff;
          curr = next;
        } else break;
      }
      if (months > 0 || parts.length > 0) {
        parts.push(months + ' Month' + (months !== 1 ? 's' : ''));
      }
    }

    if (showWeek) {
      const weeks = Math.floor(remaining / 7);
      remaining %= 7;
      if (weeks > 0 || parts.length > 0) {
        parts.push(weeks + ' Week' + (weeks !== 1 ? 's' : ''));
      }
    }

    if (showDay) {
      if (remaining > 0 || parts.length === 0) {
        parts.push(remaining + ' Day' + (remaining !== 1 ? 's' : ''));
      }
    }

    return parts.join(', ') || '0 Days';
  };

  const Checkbox = ({ checked, onPress, label }) => (
    <TouchableOpacity style={styles.checkboxRow} onPress={onPress}>
      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
        {checked && <Text style={styles.checkmark}>✓</Text>}
      </View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </TouchableOpacity>
  );

  // Mobile Layout - matches the mobile app exactly
  if (isMobile) {
    return (
      <>
        <Helmet>
          <title>Date Calculator - Numerology & Duration Calculator | Gematria Calculator</title>
          <meta name="description" content="Calculate date numerology, life path numbers, and duration between dates. Free online date calculator with numerological insights." />
          <meta name="keywords" content="date calculator, numerology calculator, life path number, date duration, date numerology" />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://gematriacalculator.xyz/dates" />
          <meta property="og:title" content="Date Calculator - Numerology & Duration Calculator" />
          <meta property="og:description" content="Calculate date numerology, life path numbers, and duration between dates. Free online date calculator with numerological insights." />
          <meta property="og:image" content="https://gematriacalculator.xyz/images/gematria-og-image.jpg" />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://gematriacalculator.xyz/dates" />
          <meta property="twitter:title" content="Date Calculator - Numerology & Duration Calculator" />
          <meta property="twitter:description" content="Calculate date numerology, life path numbers, and duration between dates." />
          <meta property="twitter:image" content="https://gematriacalculator.xyz/images/gematria-og-image.jpg" />
          
          {/* Canonical URL */}
          <link rel="canonical" href="https://gematriacalculator.xyz/dates" />
        </Helmet>

        <ScrollView style={styles.mobileContainer} contentContainerStyle={styles.mobileScrollContent}>
        <Text style={styles.mobileTitle}>Date Calculator</Text>
        
        <View style={styles.mobileDateCard}>
          <Text style={styles.mobileDateLabel}>Start Date</Text>
          <View style={styles.mobileDateInputRow}>
            <TextInput style={styles.mobileDateInput} value={startMonth} onChangeText={setStartMonth} maxLength={2} placeholder="MM" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
            <Text style={styles.mobileSeparator}>/</Text>
            <TextInput style={styles.mobileDateInput} value={startDay} onChangeText={setStartDay} maxLength={2} placeholder="DD" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
            <Text style={styles.mobileSeparator}>/</Text>
            <TextInput style={[styles.mobileDateInput, styles.mobileYearInput]} value={startYear} onChangeText={setStartYear} maxLength={4} placeholder="YYYY" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
          </View>
          <Text style={styles.mobileDatePreview}>{formatDate(startMonth, startDay, startYear)}</Text>
        </View>

        <View style={styles.mobileDateCard}>
          <Text style={styles.mobileDateLabel}>End Date</Text>
          <View style={styles.mobileDateInputRow}>
            <TextInput style={styles.mobileDateInput} value={endMonth} onChangeText={setEndMonth} maxLength={2} placeholder="MM" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
            <Text style={styles.mobileSeparator}>/</Text>
            <TextInput style={styles.mobileDateInput} value={endDay} onChangeText={setEndDay} maxLength={2} placeholder="DD" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
            <Text style={styles.mobileSeparator}>/</Text>
            <TextInput style={[styles.mobileDateInput, styles.mobileYearInput]} value={endYear} onChangeText={setEndYear} maxLength={4} placeholder="YYYY" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
          </View>
          <Text style={styles.mobileDatePreview}>{formatDate(endMonth, endDay, endYear)}</Text>
        </View>

        <TouchableOpacity style={styles.mobileToggleRow} onPress={() => setIncludeEndDate(!includeEndDate)}>
          <View style={[styles.mobileCheckbox, includeEndDate && styles.mobileCheckboxChecked]}>
            {includeEndDate && <Text style={styles.mobileCheckmark}>✓</Text>}
          </View>
          <Text style={styles.mobileToggleText}>Include end date in calculation</Text>
        </TouchableOpacity>

        <View style={styles.mobileDurationOptions}>
          <Text style={styles.mobileDurationTitle}>Select Durations to View:</Text>
          <View style={styles.mobileDurationRow}>
            <Checkbox checked={showYear} onPress={() => setShowYear(!showYear)} label="Year" />
            <Checkbox checked={showMonth} onPress={() => setShowMonth(!showMonth)} label="Month" />
            <Checkbox checked={showWeek} onPress={() => setShowWeek(!showWeek)} label="Week" />
            <Checkbox checked={showDay} onPress={() => setShowDay(!showDay)} label="Day" />
          </View>
        </View>

        {results && (
          <>
            <View style={styles.mobileDurationCard}>
              <Text style={styles.mobileDurationText}>{getDurationDisplay()}</Text>
              <Text style={styles.mobileTotalDays}>{results.duration.totalDays.toLocaleString()} Total Days</Text>
            </View>

            <View style={styles.mobileTwoColumn}>
              <View style={styles.mobileColumn}>
                <Text style={styles.mobileColumnTitle}>Start ({getMonthName(startMonth)}-{startDay}-{startYear})</Text>
                <View style={styles.mobileHighlightRow}>
                  <Text style={styles.mobileHighlightLabel}>Day of Year:</Text>
                  <Text style={styles.mobileHighlightValue}>{results.startDayOfYear}</Text>
                </View>
                <View style={styles.mobileHighlightRow}>
                  <Text style={styles.mobileHighlightLabel}>Days Left:</Text>
                  <Text style={styles.mobileHighlightValue}>{results.startDaysLeft}</Text>
                </View>
                {results.startNumerology.map((item, index) => (
                  <View key={index} style={styles.mobileNumRow}>
                    <Text style={styles.mobileNumLabel}>{item.label}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.mobileNumValue}>{item.value.toLocaleString()}</Text>
                      <WebNumberMeaning number={item.value} />
                    </View>
                  </View>
                ))}
              </View>

              <View style={styles.mobileColumn}>
                <Text style={styles.mobileColumnTitle}>End ({getMonthName(endMonth)}-{endDay}-{endYear})</Text>
                <View style={styles.mobileHighlightRow}>
                  <Text style={styles.mobileHighlightLabel}>Day of Year:</Text>
                  <Text style={styles.mobileHighlightValue}>{results.endDayOfYear}</Text>
                </View>
                <View style={styles.mobileHighlightRow}>
                  <Text style={styles.mobileHighlightLabel}>Days Left:</Text>
                  <Text style={styles.mobileHighlightValue}>{results.endDaysLeft}</Text>
                </View>
                {results.endNumerology.map((item, index) => (
                  <View key={index} style={styles.mobileNumRow}>
                    <Text style={styles.mobileNumLabel}>{item.label}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.mobileNumValue}>{item.value.toLocaleString()}</Text>
                      <WebNumberMeaning number={item.value} />
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </>
        )}
      </ScrollView>
      </>
    );
  }

  // Desktop Layout
  return (
    <>
      <Helmet>
        <title>Date Calculator - Numerology & Duration Calculator | Gematria Calculator</title>
        <meta name="description" content="Calculate date numerology, life path numbers, and duration between dates. Free online date calculator with numerological insights." />
        <meta name="keywords" content="date calculator, numerology calculator, life path number, date duration, date numerology" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gematriacalculator.xyz/dates" />
        <meta property="og:title" content="Date Calculator - Numerology & Duration Calculator" />
        <meta property="og:description" content="Calculate date numerology, life path numbers, and duration between dates. Free online date calculator with numerological insights." />
        <meta property="og:image" content="https://gematriacalculator.xyz/images/gematria-og-image.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://gematriacalculator.xyz/dates" />
        <meta property="twitter:title" content="Date Calculator - Numerology & Duration Calculator" />
        <meta property="twitter:description" content="Calculate date numerology, life path numbers, and duration between dates." />
        <meta property="twitter:image" content="https://gematriacalculator.xyz/images/gematria-og-image.jpg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://gematriacalculator.xyz/dates" />
      </Helmet>

      <ScrollView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.pageTitle}>Date Calculator</Text>
          <Text style={styles.pageSubtitle}>Calculate the time between two dates with numerology analysis</Text>
        </View>

        <View style={styles.mainLayout}>
          <View style={styles.leftColumn}>
            <View style={styles.optionsCard}>
              <Text style={styles.sectionTitle}>Options</Text>
              <Checkbox checked={includeEndDate} onPress={() => setIncludeEndDate(!includeEndDate)} label="Include end date" />
              <View style={styles.divider} />
              <Text style={styles.optionSubtitle}>Duration Units</Text>
              <Checkbox checked={showYear} onPress={() => setShowYear(!showYear)} label="Years" />
              <Checkbox checked={showMonth} onPress={() => setShowMonth(!showMonth)} label="Months" />
              <Checkbox checked={showWeek} onPress={() => setShowWeek(!showWeek)} label="Weeks" />
              <Checkbox checked={showDay} onPress={() => setShowDay(!showDay)} label="Days" />
            </View>
          </View>

          <View style={styles.centerColumn}>
            <View style={styles.dateInputsRow}>
              <View style={styles.dateCard}>
                <Text style={styles.dateCardLabel}>Start Date</Text>
                <View style={styles.dateInputContainer}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Month</Text>
                    <TextInput style={styles.dateInput} value={startMonth} onChangeText={setStartMonth} maxLength={2} placeholder="MM" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
                  </View>
                  <Text style={styles.dateDivider}>/</Text>
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Day</Text>
                    <TextInput style={styles.dateInput} value={startDay} onChangeText={setStartDay} maxLength={2} placeholder="DD" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
                  </View>
                  <Text style={styles.dateDivider}>/</Text>
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Year</Text>
                    <TextInput style={[styles.dateInput, styles.yearInput]} value={startYear} onChangeText={setStartYear} maxLength={4} placeholder="YYYY" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
                  </View>
                </View>
                <Text style={styles.datePreview}>{formatDate(startMonth, startDay, startYear)}</Text>
              </View>
              <View style={styles.arrowContainer}><Text style={styles.arrow}>→</Text></View>
              <View style={styles.dateCard}>
                <Text style={styles.dateCardLabel}>End Date</Text>
                <View style={styles.dateInputContainer}>
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Month</Text>
                    <TextInput style={styles.dateInput} value={endMonth} onChangeText={setEndMonth} maxLength={2} placeholder="MM" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
                  </View>
                  <Text style={styles.dateDivider}>/</Text>
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Day</Text>
                    <TextInput style={styles.dateInput} value={endDay} onChangeText={setEndDay} maxLength={2} placeholder="DD" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
                  </View>
                  <Text style={styles.dateDivider}>/</Text>
                  <View style={styles.inputGroup}>
                    <Text style={styles.inputLabel}>Year</Text>
                    <TextInput style={[styles.dateInput, styles.yearInput]} value={endYear} onChangeText={setEndYear} maxLength={4} placeholder="YYYY" placeholderTextColor={colors.textSecondary} selectionColor={colors.primary} />
                  </View>
                </View>
                <Text style={styles.datePreview}>{formatDate(endMonth, endDay, endYear)}</Text>
              </View>
            </View>

            {results && (
              <View style={styles.durationCard}>
                <Text style={styles.durationLabel}>Time Between Dates</Text>
                <Text style={styles.durationText}>{getDurationDisplay()}</Text>
                <Text style={styles.totalDaysText}>{results.duration.totalDays.toLocaleString()} total days</Text>
                <TouchableOpacity 
                  style={[styles.saveButton, saveStatus === 'success' && styles.saveButtonSuccess]} 
                  onPress={handleSaveToResearch}
                >
                  <Text style={styles.saveButtonText}>
                    {saveStatus === 'success' ? '✓ Saved' : '💾 Save to Research'}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {results && (
            <View style={styles.rightColumn}>
              <View style={styles.variationsCard}>
                <Text style={styles.sectionTitle}>Duration Variations</Text>
                <View style={styles.variationRow}>
                  <Text style={styles.variationValue}>{results.duration.ymd.years}</Text>
                  <Text style={styles.variationLabel}>Years,</Text>
                  <Text style={styles.variationValue}>{results.duration.ymd.months}</Text>
                  <Text style={styles.variationLabel}>Months,</Text>
                  <Text style={styles.variationValue}>{results.duration.ymd.days}</Text>
                  <Text style={styles.variationLabel}>Days</Text>
                </View>
                <View style={styles.variationRow}>
                  <Text style={styles.variationValue}>{results.duration.yearsOnly}</Text>
                  <Text style={styles.variationLabel}>Years,</Text>
                  <Text style={styles.variationValue}>{results.duration.remainingDaysAfterYears}</Text>
                  <Text style={styles.variationLabel}>Days</Text>
                </View>
                <View style={styles.variationRow}>
                  <Text style={styles.variationValue}>{results.duration.ywd.years}</Text>
                  <Text style={styles.variationLabel}>Years,</Text>
                  <Text style={styles.variationValue}>{results.duration.ywd.weeks}</Text>
                  <Text style={styles.variationLabel}>Weeks,</Text>
                  <Text style={styles.variationValue}>{results.duration.ywd.days}</Text>
                  <Text style={styles.variationLabel}>Days</Text>
                </View>
                <View style={styles.variationRow}>
                  <Text style={styles.variationValue}>{results.duration.totalMonths}</Text>
                  <Text style={styles.variationLabel}>Months,</Text>
                  <Text style={styles.variationValue}>{results.duration.remainingDaysAfterMonths}</Text>
                  <Text style={styles.variationLabel}>Days</Text>
                </View>
                <View style={styles.variationRow}>
                  <Text style={styles.variationValue}>{results.duration.totalWeeks}</Text>
                  <Text style={styles.variationLabel}>Weeks,</Text>
                  <Text style={styles.variationValue}>{results.duration.remainingDaysAfterWeeks}</Text>
                  <Text style={styles.variationLabel}>Days</Text>
                </View>
                <View style={[styles.variationRow, styles.variationRowHighlight]}>
                  <Text style={styles.variationValueLarge}>{results.duration.totalDays.toLocaleString()}</Text>
                  <Text style={styles.variationLabelLarge}>Days</Text>
                </View>
              </View>
            </View>
          )}
        </View>

        {results && (
          <View style={styles.numerologySection}>
            <Text style={styles.sectionTitleLarge}>Date Numerology</Text>
            <View style={styles.numerologyGrid}>
              <View style={styles.numerologyColumn}>
                <View style={styles.numerologyHeader}>
                  <Text style={styles.numerologyTitle}>Start Date</Text>
                  <Text style={styles.numerologyDate}>{getMonthName(startMonth)} {startDay}, {startYear}</Text>
                </View>
                <View style={styles.highlightRow}>
                  <Text style={styles.highlightLabel}>Day of Year</Text>
                  <Text style={styles.highlightValue}>{results.startDayOfYear}</Text>
                </View>
                <View style={styles.highlightRow}>
                  <Text style={styles.highlightLabel}>Days Left in Year</Text>
                  <Text style={styles.highlightValue}>{results.startDaysLeft}</Text>
                </View>
                <View style={styles.numerologyDivider} />
                {results.startNumerology.map((item, index) => (
                  <View key={index} style={styles.numRow}>
                    <Text style={styles.numFormula}>{item.label}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.numResult}>{item.value.toLocaleString()}</Text>
                      <WebNumberMeaning number={item.value} />
                    </View>
                  </View>
                ))}
              </View>
              <View style={styles.numerologyColumn}>
                <View style={styles.numerologyHeader}>
                  <Text style={styles.numerologyTitle}>End Date</Text>
                  <Text style={styles.numerologyDate}>{getMonthName(endMonth)} {endDay}, {endYear}</Text>
                </View>
                <View style={styles.highlightRow}>
                  <Text style={styles.highlightLabel}>Day of Year</Text>
                  <Text style={styles.highlightValue}>{results.endDayOfYear}</Text>
                </View>
                <View style={styles.highlightRow}>
                  <Text style={styles.highlightLabel}>Days Left in Year</Text>
                  <Text style={styles.highlightValue}>{results.endDaysLeft}</Text>
                </View>
                <View style={styles.numerologyDivider} />
                {results.endNumerology.map((item, index) => (
                  <View key={index} style={styles.numRow}>
                    <Text style={styles.numFormula}>{item.label}</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.numResult}>{item.value.toLocaleString()}</Text>
                      <WebNumberMeaning number={item.value} />
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}
      </View>
    </ScrollView>
    </>
  );
}

const createStyles = (colors) => StyleSheet.create({
  // Mobile styles - matching the mobile app exactly
  mobileContainer: { flex: 1, backgroundColor: colors.background },
  mobileScrollContent: { padding: 16, paddingBottom: 100 },
  mobileTitle: { fontSize: 24, fontWeight: 'bold', color: colors.text, textAlign: 'center', marginBottom: 8 },
  mobileDateCard: { backgroundColor: colors.surfaceElevated, borderRadius: 12, padding: 16, marginBottom: 12 },
  mobileDateLabel: { fontSize: 16, fontWeight: '600', color: colors.text, marginBottom: 12 },
  mobileDateInputRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  mobileDateInput: { backgroundColor: colors.inputBackground, borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 10, fontSize: 16, color: colors.text, textAlign: 'center', width: 50, outlineColor: colors.primary },
  mobileYearInput: { width: 70 },
  mobileSeparator: { fontSize: 20, color: colors.textSecondary, marginHorizontal: 6 },
  mobileDatePreview: { fontSize: 14, color: colors.textSecondary, textAlign: 'center', marginTop: 10 },
  mobileToggleRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.surface, padding: 14, borderRadius: 12, marginBottom: 12 },
  mobileCheckbox: { width: 22, height: 22, borderWidth: 2, borderColor: colors.primary, borderRadius: 4, alignItems: 'center', justifyContent: 'center', marginRight: 10 },
  mobileCheckboxChecked: { backgroundColor: colors.primary },
  mobileCheckmark: { color: colors.onPrimary, fontSize: 14, fontWeight: 'bold' },
  mobileToggleText: { fontSize: 15, color: colors.text },
  mobileDurationOptions: { backgroundColor: colors.surface, borderRadius: 12, padding: 14, marginBottom: 12 },
  mobileDurationTitle: { fontSize: 14, fontWeight: '600', color: colors.text, marginBottom: 10 },
  mobileDurationRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  mobileDurationCard: { backgroundColor: colors.primary, borderRadius: 12, padding: 16, marginBottom: 16, alignItems: 'center' },
  mobileDurationText: { fontSize: 18, fontWeight: 'bold', color: colors.onPrimary, textAlign: 'center' },
  mobileTotalDays: { fontSize: 14, color: colors.onPrimary, opacity: 0.9, marginTop: 6 },
  mobileTwoColumn: { flexDirection: 'row', gap: 10 },
  mobileColumn: { flex: 1, backgroundColor: colors.surfaceElevated, borderRadius: 12, padding: 12 },
  mobileColumnTitle: { fontSize: 13, fontWeight: 'bold', color: colors.primary, marginBottom: 10, textAlign: 'center' },
  mobileHighlightRow: { flexDirection: 'row', justifyContent: 'space-between', backgroundColor: colors.primarySurface, padding: 8, borderRadius: 6, marginBottom: 6 },
  mobileHighlightLabel: { fontSize: 12, color: colors.primary, fontWeight: '500' },
  mobileHighlightValue: { fontSize: 12, fontWeight: 'bold', color: colors.text },
  mobileNumRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: colors.lightBorder },
  mobileNumLabel: { fontSize: 11, color: colors.textSecondary, flex: 1 },
  mobileNumValue: { fontSize: 12, fontWeight: '600', color: colors.text },

  // Shared checkbox styles
  checkboxRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  checkbox: { width: 18, height: 18, borderWidth: 2, borderColor: colors.primary, borderRadius: 3, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  checkboxChecked: { backgroundColor: colors.primary },
  checkmark: { color: colors.onPrimary, fontSize: 12, fontWeight: 'bold' },
  checkboxLabel: { fontSize: 14, color: colors.text },

  // Desktop styles
  container: { flex: 1, backgroundColor: colors.background },
  content: { maxWidth: 1200, alignSelf: 'center', width: '100%', padding: 24 },
  header: { marginBottom: 24 },
  pageTitle: { fontSize: 28, fontWeight: 'bold', color: colors.text, marginBottom: 4 },
  pageSubtitle: { fontSize: 16, color: colors.textSecondary },
  mainLayout: { flexDirection: 'row', gap: 20, marginBottom: 24 },
  leftColumn: { width: 180 },
  centerColumn: { flex: 1 },
  rightColumn: { width: 200 },
  optionsCard: { backgroundColor: colors.surfaceElevated, borderRadius: 12, padding: 16, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', color: colors.text, marginBottom: 12 },
  divider: { height: 1, backgroundColor: colors.divider, marginVertical: 12 },
  optionSubtitle: { fontSize: 13, color: colors.textSecondary, marginBottom: 8 },
  dateInputsRow: { flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 16 },
  dateCard: { flex: 1, backgroundColor: colors.surfaceElevated, borderRadius: 12, padding: 16, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  dateCardLabel: { fontSize: 14, fontWeight: '600', color: colors.text, marginBottom: 12, textAlign: 'center' },
  dateInputContainer: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'center', gap: 4 },
  inputGroup: { alignItems: 'center' },
  inputLabel: { fontSize: 11, color: colors.textSecondary, marginBottom: 4 },
  dateInput: { backgroundColor: colors.inputBackground, borderWidth: 1, borderColor: colors.border, borderRadius: 6, padding: 8, fontSize: 16, color: colors.text, textAlign: 'center', width: 50, outlineColor: colors.primary },
  yearInput: { width: 70 },
  dateDivider: { fontSize: 18, color: colors.textSecondary, paddingBottom: 8 },
  datePreview: { fontSize: 13, color: colors.textSecondary, textAlign: 'center', marginTop: 10 },
  arrowContainer: { paddingBottom: 20 },
  arrow: { fontSize: 24, color: colors.primary },
  durationCard: { backgroundColor: colors.primary, borderRadius: 12, padding: 16, alignItems: 'center' },
  durationLabel: { fontSize: 13, color: colors.onPrimary, opacity: 0.8, marginBottom: 4 },
  durationText: { fontSize: 20, fontWeight: 'bold', color: colors.onPrimary },
  totalDaysText: { fontSize: 14, color: colors.onPrimary, opacity: 0.9, marginTop: 4 },
  variationsCard: { backgroundColor: colors.surfaceElevated, borderRadius: 12, padding: 14, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  variationRow: { flexDirection: 'row', alignItems: 'baseline', gap: 4, paddingVertical: 4 },
  variationValue: { fontSize: 16, fontWeight: 'bold', color: colors.text },
  variationLabel: { fontSize: 13, color: colors.textSecondary },
  variationRowHighlight: { backgroundColor: colors.primarySurface, borderRadius: 6, padding: 8, marginTop: 8 },
  variationValueLarge: { fontSize: 20, fontWeight: 'bold', color: colors.primary },
  variationLabelLarge: { fontSize: 14, color: colors.primary },
  numerologySection: { marginTop: 8 },
  sectionTitleLarge: { fontSize: 20, fontWeight: 'bold', color: colors.text, marginBottom: 16 },
  numerologyGrid: { flexDirection: 'row', gap: 20 },
  numerologyColumn: { flex: 1, backgroundColor: colors.surfaceElevated, borderRadius: 12, padding: 16, shadowColor: colors.shadow, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  numerologyHeader: { marginBottom: 12 },
  numerologyTitle: { fontSize: 16, fontWeight: 'bold', color: colors.text },
  numerologyDate: { fontSize: 13, color: colors.textSecondary },
  highlightRow: { flexDirection: 'row', justifyContent: 'space-between', backgroundColor: colors.primarySurface, padding: 10, borderRadius: 6, marginBottom: 6 },
  highlightLabel: { fontSize: 13, color: colors.primary, fontWeight: '500' },
  highlightValue: { fontSize: 14, fontWeight: 'bold', color: colors.text },
  numerologyDivider: { height: 1, backgroundColor: colors.divider, marginVertical: 10 },
  numRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: colors.lightBorder },
  numFormula: { fontSize: 13, color: colors.textSecondary, flex: 1 },
  numResult: { fontSize: 14, fontWeight: '600', color: colors.text },
  saveButton: { backgroundColor: 'rgba(255,255,255,0.2)', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginTop: 12 },
  saveButtonText: { color: colors.onPrimary, fontSize: 13, fontWeight: 'bold' },
  saveButtonSuccess: { backgroundColor: colors.success },
});
